import React, { useEffect, useState } from 'react';
import { FormGenericContainer } from '../FormGenericContainer';
import { EventDisplayOption, EventTemplate } from '@go-shared/types/eventTemplate';
import {
  BubbleAlert,
  BubbleButton,
  BubbleCheckbox,
  BubbleFormGroup,
  BubbleHintSelect,
  BubbleImageSelector,
  BubbleInput,
  BubbleOverlay,
  BubbleSelect,
  BubbleTextArea,
  BubbleTypeaheadSimple,
} from '../BubbleUI';
import { EVENT_DISPLAY_MAP, EVENT_DISPLAY_TYPES } from '@go-shared/lib/event';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { Time } from '@go-shared/types/time';
import { addMinutesToTime, timeToMinutes } from '@go-shared/lib/time';
import { BareSailingDay } from '@go-shared/types/sailing';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { generateReplaceDict, generateTranslDict } from '@go-shared/lib/mergedVars';
import { captureBetweenDollars, safeIdCompare } from '@go-shared/lib/utils';
import { MergedVariables } from '@go-shared/types/mergedVars';
import { Language } from '@go-shared/types/info';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { Alert } from '@spark-labs/avocado-ui';
import StaffAllocationsPanel from '../StaffAllocationsPanel';
import { StaffAllocation } from '@go-shared/types/staffAllocation';
import { mapBy, onlyUnique } from '@go-shared/lib/functional';
import McmVisibilityTable from '../McmVisibilityTable';
import { Mcm } from '@go-shared/types/mcm';
import { Plan } from '@go-shared/types/plan';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import { CanvasEventFormMode } from './CanvasEventForm';
import AudienceVisibilityTable from '../AudienceVisibilityTable';
import { Audience } from '@go-shared/types/audience';
import { translateEventTemplate } from '@go-shared/lib/eventTemplate';
import { EventTemplateTranslation } from '@go-shared/types/translation';
import { CallToActionsInstance } from '@go-shared/types/dailyProgram';
import { CallToActionsManagement } from '@go-client/components/BasicForms/CallToActionsManagement';

interface EventFormDataBase {
  venue: VenueTemplate;
  transferDay?: BareSailingDay;
  startTime?: Time;
  endTime?: Time;
  interactiveFeedDisplay: EventDisplayOption | undefined;
  hidden: boolean;
  callToActions: CallToActionsInstance;
  deckDisplayName: string;
  venueDisplayName: string;
  staffAllocations: StaffAllocation[];
  mergedVars: MergedVariables;
  applyStaffToPlan: boolean;
  imageOverride?: string;
  highlights: string[];
  mcm?: Mcm;
  audience?: Audience;
}

export type EventFormData = EventFormDataBase & {
  _dirty: boolean;
  _errors: Partial<EventFormError>;
};

type EventFormError = { [K in keyof EventFormDataBase]: string };

function DEFAULT_FORM_DATA(
  mode: CanvasEventFormMode,
  venueTemplates: VenueTemplate[],
  selectedEvtTemplate: EventTemplate,
  languages: Language[],
  mergedVarKeys: string[]
): EventFormData {
  const startTime = mode.event.startTime || { hour: 0, minute: 0 };
  const endTime =
    mode.event.endTime || addMinutesToTime(startTime, selectedEvtTemplate.defaultDuration);

  return {
    transferDay: mode.dayTransfer?.current,
    mcm: mode.event.mcm,
    audience: mode.event.audience,
    venue: mode.event.venue ?? mode.gridLoc?.venue ?? venueTemplates[0],
    startTime,
    endTime,
    interactiveFeedDisplay:
      mode.event.interactiveFeedDisplay ?? selectedEvtTemplate.interactiveFeedDisplay,
    hidden: mode.event.hidden != undefined ? mode.event.hidden : selectedEvtTemplate.isPrivate,
    deckDisplayName: mode.event.deckDisplayName ?? '',
    venueDisplayName: mode.event.venueDisplayName ?? '',
    callToActions: mode.event.callToActions ?? {
      syncWithTemplate: true,
      values: selectedEvtTemplate.callToActions,
    },
    staffAllocations: mode.event.staffAllocations,
    mergedVars: {
      replacements: generateReplaceDict(mergedVarKeys, mode.event.mergedVars),
      translations: generateTranslDict(
        mergedVarKeys,
        languages.map(l => l.key),
        mode.event.mergedVars
      ),
    },
    imageOverride: mode.event.imageOverride,
    highlights: mode.event.highlights || [],
    applyStaffToPlan: false,
    _dirty: false,
    _errors: {},
  };
}

interface ModifyEventDetailsPageProps {
  mode: CanvasEventFormMode;
  plan: Plan;
  selectedEvtTemplate: EventTemplate;
  venueTemplates: VenueTemplate[];
  staffTemplates: StaffTemplate[];
  eventTemplateTranslations: EventTemplateTranslation[];
  title?: string;
  submitTitle?: string;
  onGoBack?: () => void;
  onClose?: () => void;
  onDeleteEvent?: (eventId: string) => void;
  onSubmit?: (eventForm: EventFormData) => Promise<void>;
}

type EventDetailsPageTab = 'basic' | 'staff' | 'mergedVars';

export default function ModifyEventDetailsPage(props: ModifyEventDetailsPageProps) {
  const {
    mode,
    plan,
    selectedEvtTemplate,
    venueTemplates,
    staffTemplates,
    eventTemplateTranslations,
  } = props;

  const { brand, languages, defaultLanguage, settings } = useServerInfo();
  const [selectedLang, setSelectedLang] = useState(defaultLanguage.key);
  const [loading, setLoading] = useState(false);

  const eventTemplateTranslationsMap = mapBy(eventTemplateTranslations, 'refers');
  const evtTmpl =
    (selectedLang != defaultLanguage.key
      ? translateEventTemplate(
          selectedEvtTemplate,
          eventTemplateTranslationsMap[selectedEvtTemplate._id],
          selectedLang
        )
      : selectedEvtTemplate) || selectedEvtTemplate;

  const dayOptions = mode.dayTransfer?.options;
  const dayStartMinutes = timeToMinutes(plan.dayStart);
  const mergedVarKeys = captureBetweenDollars(selectedEvtTemplate.title)
    .concat(captureBetweenDollars(selectedEvtTemplate.description))
    .map(m => m[1]);

  const [formData, setFormData] = useState<EventFormData>(
    DEFAULT_FORM_DATA(mode, venueTemplates, evtTmpl, languages, mergedVarKeys)
  );
  const [activeTab, setActiveTab] = useState<EventDetailsPageTab>('basic');

  let levelLabel = getPropertyConfiguration(plan.property.type, 'levelLabel');

  useEffect(() => {
    resetForm();
  }, [mode, plan, selectedEvtTemplate]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(mode, venueTemplates, evtTmpl, languages, mergedVarKeys));
  }

  function changeForm<T extends keyof EventFormDataBase>(key: T, val: EventFormDataBase[T]) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  function sameInteractiveFeedDisplayValueFromTemplate(
    formDataValue: EventDisplayOption,
    templateValue: EventDisplayOption
  ) {
    return formDataValue === templateValue;
  }

  function getInteractiveDisplayFeedLabel(id: EventDisplayOption) {
    const label = EVENT_DISPLAY_MAP[id].label;
    const extra = sameInteractiveFeedDisplayValueFromTemplate(
      selectedEvtTemplate.interactiveFeedDisplay,
      EVENT_DISPLAY_MAP[id].value
    )
      ? "(Library's default)"
      : '';

    return `${label} ${extra}`;
  }

  async function handleSubmit() {
    if (!props.onSubmit) return;

    try {
      setLoading(true);
      await props.onSubmit(formData);
    } finally {
      setLoading(false);
    }
  }

  const highlightsDict = mapBy(brand.eventHighlights, '_id');

  return (
    <div className="cef-2">
      <FormGenericContainer
        className="absolute top-0 left-0 w-full h-full flex flex-col"
        bodyClassName="p-0-i flex-grow relative bg-eggshell"
        footerClassName="p-4-i border-t"
        footer={() => (
          <>
            {evtTmpl && (
              <div className="text-slate-dark">
                <div>
                  Event Selected:
                  <span className="font-semibold"> {evtTmpl.title}</span>
                </div>
              </div>
            )}
            <div className="ml-auto" />
            {(mode.type !== 'edit' || settings.ALLOW_EVENT_TEMPLATE_CHANGE) && (
              <BubbleButton variant="navy" inverse onClick={props.onGoBack}>
                Go Back
              </BubbleButton>
            )}
            {mode.type === 'edit' && (
              <BubbleButton
                variant="crimson"
                inverse
                onClick={async () => {
                  if (mode.event._id && props.onDeleteEvent) {
                    const yes = await Alert.question(
                      'Are you sure you wish to delete this event from the canvas?'
                    );
                    if (!yes) return;
                    props.onDeleteEvent(mode.event._id);
                  }
                }}
                icon="trash-alt"
              >
                Delete Event
              </BubbleButton>
            )}
            <BubbleOverlay loading={loading}>
              <BubbleButton onClick={handleSubmit} variant="navy">
                {props.submitTitle || 'Finish: Add Event'}
              </BubbleButton>
            </BubbleOverlay>
          </>
        )}
      >
        <div className="p-6">
          <div className="flex items-start mb-8">
            <div>
              <h1 className="text-lg text-slate-dark font-semibold">
                {props.title || 'Add Event'}
              </h1>
              <h2 className="text-2xl text-navy">2. Enter Day Details</h2>
            </div>
            <BubbleButton
              onClick={props.onClose}
              className="ml-auto"
              variant="navy"
              inverse
              icon="xmark-large"
              iconSize="xl"
            />
          </div>
          <div className="flex items-center -mx-6 mb-5">
            <BubbleButton
              className={`h-full ${activeTab === 'basic' ? '' : 'font-normal-i'}`}
              appearance="squared"
              variant={activeTab === 'basic' ? 'navy' : 'smoke'}
              block
              onClick={() => setActiveTab('basic')}
            >
              Instance Information
            </BubbleButton>
            <BubbleButton
              className={`h-full ${activeTab === 'staff' ? '' : 'font-normal-i'}`}
              appearance="squared"
              variant={activeTab === 'staff' ? 'navy' : 'smoke'}
              block
              onClick={() => setActiveTab('staff')}
            >
              Staff Assignments
            </BubbleButton>
            <BubbleButton
              className={`h-full ${activeTab === 'mergedVars' ? '' : 'font-normal-i'}`}
              appearance="squared"
              variant={activeTab === 'mergedVars' ? 'navy' : 'smoke'}
              block
              onClick={() => setActiveTab('mergedVars')}
            >
              Merged Variables
            </BubbleButton>
          </div>
          {activeTab === 'basic' ? (
            <div className="grid grid-cols-6 gap-4 items-start">
              <BubbleFormGroup label="Image Override" className="col-span-6">
                <BubbleImageSelector
                  disableUpload
                  value={formData.imageOverride ? [formData.imageOverride] : []}
                  onValue={val => changeForm('imageOverride', val[0])}
                  maxItems={1}
                />
              </BubbleFormGroup>

              <BubbleFormGroup
                label="Venue"
                className="col-span-2"
                error={formData._errors.venue}
                asterisk
              >
                <BubbleHintSelect
                  value={formData.venue}
                  allowUndefined={false}
                  options={venueTemplates.filter(
                    vt => safeIdCompare(vt._id, formData.venue._id) || !vt.archived
                  )}
                  onValue={val => changeForm('venue', val)}
                  labelExtractor={vt => vt.name}
                  internalValueExtractor={vt => vt._id}
                  menuClassName="max-h-80 overflow-y-auto"
                />
              </BubbleFormGroup>

              <BubbleFormGroup
                label="Start Time"
                className="col-span-2"
                error={formData._errors.startTime}
                asterisk
              >
                <BubbleInput
                  block
                  type="time"
                  value={formData.startTime}
                  onValue={val => changeForm('startTime', val)}
                />
              </BubbleFormGroup>
              <BubbleFormGroup
                label="End Time"
                className="col-span-2"
                error={formData._errors.endTime}
                asterisk
              >
                <BubbleInput
                  block
                  type="time"
                  value={formData.endTime}
                  onValue={val => changeForm('endTime', val)}
                />
              </BubbleFormGroup>
              <BubbleFormGroup
                label="Events Venue Name"
                className="col-span-2"
                error={formData._errors.venueDisplayName}
              >
                <BubbleInput
                  block
                  type="text"
                  value={formData.venueDisplayName}
                  onValue={val => changeForm('venueDisplayName', val)}
                />
              </BubbleFormGroup>
              <BubbleFormGroup
                label={`Events ${levelLabel} Name`}
                className="col-span-2"
                error={formData._errors.deckDisplayName}
              >
                <BubbleInput
                  block
                  type="text"
                  value={formData.deckDisplayName}
                  onValue={val => changeForm('deckDisplayName', val)}
                />
              </BubbleFormGroup>
              <BubbleFormGroup
                label="Time Display"
                className="col-span-2"
                error={formData._errors.interactiveFeedDisplay}
              >
                <BubbleSelect
                  block
                  allowUndefined={false}
                  labelExtractor={id => getInteractiveDisplayFeedLabel(id)}
                  value={
                    formData.interactiveFeedDisplay ?? selectedEvtTemplate.interactiveFeedDisplay
                  }
                  options={EVENT_DISPLAY_TYPES}
                  onValue={val =>
                    changeForm(
                      'interactiveFeedDisplay',
                      sameInteractiveFeedDisplayValueFromTemplate(
                        val,
                        selectedEvtTemplate.interactiveFeedDisplay
                      )
                        ? undefined
                        : val
                    )
                  }
                />
              </BubbleFormGroup>
              {dayOptions && (
                <BubbleFormGroup
                  label="Day"
                  error={formData._errors.transferDay}
                  className="col-span-3"
                >
                  <BubbleSelect
                    block
                    allowUndefined={false}
                    options={dayOptions}
                    value={formData.transferDay}
                    labelExtractor={e => e.name}
                    onValue={val => changeForm('transferDay', val)}
                  />
                </BubbleFormGroup>
              )}
              <BubbleFormGroup
                label="Highlights"
                className={dayOptions ? 'col-span-3' : 'col-span-6'}
              >
                <BubbleTypeaheadSimple
                  block
                  options={brand.eventHighlights.map(a => a._id)}
                  value={formData.highlights}
                  labelExtractor={a => highlightsDict[a]?.name}
                  onValue={val =>
                    changeForm('highlights', val.filter(v => highlightsDict[v]).filter(onlyUnique))
                  }
                />
              </BubbleFormGroup>
              <hr className="col-span-6 my-1" />
              <div className="col-span-6">
                <CallToActionsManagement
                  sourceCtas={formData.callToActions}
                  parentCtas={evtTmpl.callToActions}
                  onUpdate={(newCta: CallToActionsInstance) => {
                    changeForm('callToActions', newCta);
                  }}
                />
              </div>
              <hr className="col-span-6 my-1" />
              <div className="col-span-3">
                <BubbleFormGroup label="Endpoints">
                  <McmVisibilityTable
                    isTemplate={false}
                    sourceMcm={formData.mcm}
                    parentMcm={evtTmpl.mcm}
                    onUpdateMcm={(newMcm: Mcm) => changeForm('mcm', newMcm)}
                  />
                </BubbleFormGroup>
              </div>
              <div className="col-span-3">
                <BubbleFormGroup label="Audience">
                  <AudienceVisibilityTable
                    isTemplate={false}
                    sourceAud={formData.audience}
                    parentAud={evtTmpl.audience}
                    onUpdateAudience={(newAud: Audience) => changeForm('audience', newAud)}
                  />
                </BubbleFormGroup>
              </div>
            </div>
          ) : activeTab === 'staff' ? (
            <div>
              {formData.startTime && formData.endTime ? (
                <>
                  <StaffAllocationsPanel
                    eventDuration={
                      timeToMinutes(formData.endTime, dayStartMinutes) -
                      timeToMinutes(formData.startTime, dayStartMinutes)
                    }
                    staffAllocations={formData.staffAllocations}
                    relatedEventTime={{
                      eventStart: formData.startTime,
                      dayStart: plan.dayStart,
                      hourFormat: brand.hourFormat,
                    }}
                    staffTemplates={staffTemplates}
                    onChange={val => changeForm('staffAllocations', val as StaffAllocation[])}
                  />
                  <BubbleFormGroup className="mt-5" label="Assignment Scope">
                    <div className="flex items-center space-x-4">
                      <BubbleCheckbox
                        appearance="rounded"
                        checked={formData.applyStaffToPlan === false}
                        onValue={_ => changeForm('applyStaffToPlan', false)}
                        label="This Occurrence Only"
                      />

                      <BubbleCheckbox
                        appearance="rounded"
                        checked={formData.applyStaffToPlan === true}
                        onValue={_ => changeForm('applyStaffToPlan', true)}
                        label="All Occurrences in Day Plan"
                      />
                    </div>
                  </BubbleFormGroup>
                </>
              ) : (
                <BubbleAlert variant="ocean">
                  No staff allocation support found for this event
                </BubbleAlert>
              )}
            </div>
          ) : (
            <div>
              {mergedVarKeys.length > 0 ? (
                <div className="h-full flex items-start bg-eggshell -mx-6">
                  <div className="sticky top-0 px-4 flex-shrink-0">
                    <div className="grid grid-cols-1">
                      <BubbleButton
                        variant="navy"
                        className="text-left"
                        inverse={selectedLang != defaultLanguage.key}
                        onClick={() => setSelectedLang(defaultLanguage.key)}
                      >
                        {defaultLanguage.englishLabel}
                      </BubbleButton>
                      {languages.map(l => (
                        <BubbleButton
                          key={l.key}
                          className="text-left"
                          onClick={() => setSelectedLang(l.key)}
                          variant="navy"
                          inverse={selectedLang != l.key}
                        >
                          {l.englishLabel}
                        </BubbleButton>
                      ))}
                    </div>
                  </div>
                  <div className="flex-grow grid gap-4 grid-cols-1 pr-4 pb-4 pt-0">
                    <div className="bg-white px-6 py-4 border grid gap-4 grid-cols-1">
                      <BubbleFormGroup label="Title">{evtTmpl.title}</BubbleFormGroup>
                      <BubbleFormGroup label="Description">{evtTmpl.description}</BubbleFormGroup>
                    </div>
                    {mergedVarKeys.map(mv => (
                      <div key={mv}>
                        {selectedLang === defaultLanguage.key ? (
                          <BubbleFormGroup label={mv}>
                            <div className="flex items-start">
                              <BubbleInput
                                type="checkbox"
                                className="mr-2"
                                inputClassName="bg-white"
                                checked={formData.mergedVars.replacements[mv].enabled}
                                onValue={checked =>
                                  changeForm('mergedVars', {
                                    ...formData.mergedVars,
                                    replacements: {
                                      ...formData.mergedVars.replacements,
                                      [mv]: {
                                        ...formData.mergedVars.replacements[mv],
                                        enabled: checked,
                                      },
                                    },
                                  })
                                }
                              />
                              <BubbleTextArea
                                block
                                className="flex-grow"
                                value={formData.mergedVars.replacements[mv].value}
                                onClick={() =>
                                  changeForm('mergedVars', {
                                    ...formData.mergedVars,
                                    replacements: {
                                      ...formData.mergedVars.replacements,
                                      [mv]: {
                                        ...formData.mergedVars.replacements[mv],
                                        enabled: true,
                                      },
                                    },
                                  })
                                }
                                onValue={val =>
                                  changeForm('mergedVars', {
                                    ...formData.mergedVars,
                                    replacements: {
                                      ...formData.mergedVars.replacements,
                                      [mv]: {
                                        ...formData.mergedVars.replacements[mv],
                                        value: val,
                                      },
                                    },
                                  })
                                }
                              />
                            </div>
                          </BubbleFormGroup>
                        ) : (
                          <BubbleFormGroup label={mv} asterisk>
                            <div className="flex items-start">
                              <BubbleInput
                                type="checkbox"
                                className="mr-2"
                                inputClassName="bg-white"
                                checked={formData.mergedVars.translations[selectedLang][mv].enabled}
                                onValue={checked =>
                                  changeForm('mergedVars', {
                                    ...formData.mergedVars,
                                    translations: {
                                      ...formData.mergedVars.translations,
                                      [selectedLang]: {
                                        ...formData.mergedVars.translations[selectedLang],
                                        [mv]: {
                                          ...formData.mergedVars.translations[selectedLang][mv],
                                          enabled: checked,
                                        },
                                      },
                                    },
                                  })
                                }
                              />
                              <BubbleTextArea
                                block
                                className="flex-grow"
                                onClick={() =>
                                  changeForm('mergedVars', {
                                    ...formData.mergedVars,
                                    translations: {
                                      ...formData.mergedVars.translations,
                                      [selectedLang]: {
                                        ...formData.mergedVars.translations[selectedLang],
                                        [mv]: {
                                          ...formData.mergedVars.translations[selectedLang][mv],
                                          enabled: true,
                                        },
                                      },
                                    },
                                  })
                                }
                                value={formData.mergedVars.translations[selectedLang][mv].value}
                                onValue={val =>
                                  changeForm('mergedVars', {
                                    ...formData.mergedVars,
                                    translations: {
                                      ...formData.mergedVars.translations,
                                      [selectedLang]: {
                                        ...formData.mergedVars.translations[selectedLang],
                                        [mv]: {
                                          ...formData.mergedVars.translations[selectedLang][mv],
                                          value: val,
                                        },
                                      },
                                    },
                                  })
                                }
                              />
                            </div>
                          </BubbleFormGroup>
                        )}
                      </div>
                    ))}
                    {selectedLang !== defaultLanguage.key && (
                      <div className="italic text-xs text-slate sticky bottom-0">
                        *Defaults to {defaultLanguage.englishLabel} if not enabled
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <BubbleAlert variant="ocean">
                  No merged variables found in title or description of this event
                </BubbleAlert>
              )}
            </div>
          )}
        </div>
      </FormGenericContainer>
    </div>
  );
}
